// [2026-11-02] ADDED — INC-2082: the What's New button, as on IncBot, IncScreen and the account portal.
//
// A PORT OF frontend-incbot src/component/Common/WhatsNewDropdown.jsx: the same button, panel and
// cards with the same class names (the `.whats-new-*` rules in platform-theme.css). Where it differs:
//
//   1. The data comes through this app's backend, GET /me/whats-new, and not straight from
//      user-auth-system. That API needs a bearer token this dashboard does not hold. The full reason
//      is above the route in proctoring-sdk-backend app/modules/me/route.py.
//   2. Read more opens the announcement's own page, which is what INC-1132 section 2.11 asks for and
//      what the account portal's widget does. IncBot's opens the list.
//   3. The button goes when the last card is marked as read (INC-1132 section 2.11), as in the
//      account portal. IncBot's stays until the page is reloaded.
//   4. There is no Redux store here, so the data lives in the useWhatsNew hook below.

import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Check } from 'lucide-react'
import { platform } from '../../api.js'
import { generateUrl } from './productLinks.js'

const ExternalLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M6.667 3.333H3.333A1.333 1.333 0 0 0 2 4.667v8A1.333 1.333 0 0 0 3.333 14h8a1.333 1.333 0 0 0 1.334-1.333V9.333"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.333 2H14v4.667M14 2 7.333 8.667"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M7 1v8M4 6l3 3 3-3M2 11h10"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const formatCategoryLabel = (category) => {
  if (!category) return ''
  return category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

// Short Description is rich text, so the card shows its plain text and truncates there, which keeps
// the markup out and the tags intact.
const toPlainText = (html = '') =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

const truncate = (value, maxChars = 160) =>
  (value.length > maxChars ? `${value.slice(0, maxChars).trimEnd()}...` : value)

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The cards this person has not read yet. No product id means no call and no button, so a bare
 *  product rail costs the customer What's New and nothing else. */
export function useWhatsNew(productId) {
  const [data, setData] = useState(null)
  const [readIds, setReadIds] = useState([])

  useEffect(() => {
    if (!productId) return undefined
    let live = true
    platform.whatsNew(productId)
      .then((result) => { if (live) setData(result) })
      .catch(() => { /* no button: news must never get in the way of the dashboard */ })
    return () => { live = false }
  }, [productId])

  // Hides the card straight away, then saves. The server is the source of truth on the next load,
  // so a failed call just brings the card back.
  const markRead = (announcementId) => {
    setReadIds((prev) => [...prev, announcementId])
    platform.markWhatsNewRead(announcementId, productId).catch(() => {})
  }

  return {
    items: (data?.announcement || []).filter((item) => !readIds.includes(item._id)),
    containerSasToken: data?.containerSasToken || '',
    markRead,
  }
}

export default function WhatsNewDropdown({ productId, whatsNew }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const { items, containerSasToken, markRead } = whatsNew

  useEffect(() => {
    if (!open) return undefined
    const onMouseDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // The update pages live on the account portal and open in a new tab, which has no idea which
  // product it came from unless it is told.
  const updatesUrl = `${generateUrl('account')}/product-enhancements`
  const query = `?productId=${productId}`

  // The attachment lives in a private container, so the link only works with the SAS token joined on.
  const handleDownload = (item) => {
    const { attachmentUrl } = item
    if (!attachmentUrl) return
    const signedUrl = containerSasToken
      ? `${attachmentUrl}${attachmentUrl.includes('?') ? '&' : '?'}${containerSasToken}`
      : attachmentUrl
    window.open(signedUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="whats-new-wrapper" ref={ref}>
      <button type="button" className="whats-new-btn-announcement" aria-expanded={open}
              onClick={() => setOpen((prev) => !prev)}>
        What's New 🚀
      </button>
      {open && (
        <div className="whats-new-dropdown">
          <div className="whats-new-header">
            <span className="whats-new-header-title">
              Latest Updates{' '}
              <a href={`${updatesUrl}${query}`} target="_blank" rel="noopener noreferrer"
                 className="whats-new-header-link" aria-label="Open all updates">
                <ExternalLinkIcon />
              </a>
            </span>
          </div>
          <div className="whats-new-body">
            {items.map((item) => (
              <div key={item._id} className="whats-new-card">
                <div className="whats-new-card-meta">
                  {item.announcementCategory && (
                    <span className="whats-new-category">{formatCategoryLabel(item.announcementCategory)}</span>
                  )}
                  {(item.validFrom || item.createdAt) && (
                    <span className="whats-new-date">{formatDate(item.validFrom || item.createdAt)}</span>
                  )}
                </div>
                {item.messageTitle && <div className="whats-new-title">{item.messageTitle}</div>}
                {item.shortDescription && (
                  <div className="whats-new-description">{truncate(toPlainText(item.shortDescription))}</div>
                )}
                <div className="whats-new-actions">
                  {item.attachmentUrl && (
                    <button type="button" className="whats-new-download-btn" onClick={() => handleDownload(item)}>
                      <DownloadIcon />
                      Download Attachment
                    </button>
                  )}
                  <button type="button" className="whats-new-download-btn" onClick={() => markRead(item._id)}>
                    <Check size={14} />
                    Mark as read
                  </button>
                  <a href={`${updatesUrl}/${item._id}${query}`} target="_blank" rel="noopener noreferrer"
                     className="whats-new-read-more">
                    Read more <ArrowUpRight size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="whats-new-footer">
            <a href={`${updatesUrl}${query}`} target="_blank" rel="noopener noreferrer">View All Updates</a>
          </div>
        </div>
      )}
    </div>
  )
}
